import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useConnection, useUserInfo } from '../../connection/Application';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { ListItem, Link, Avatar, Tooltip, Typography, Stack, Button, Divider } from '@mui/material';
import List from '@mui/material/List';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryParam } from 'use-query-params';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(0.5),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const icons = [
  'Booked',
  'For more info',
  'For your info',
  'Learn More',
  'More information',
  'New Content',
  'New Contents',
  'Note',
  'Papers - Past',
  'Past Paper',
  'Past Papers',
  'Quest n Answrs',
  'Questionaires',
  'Ref Text Book',
  'REF Txt Book',
  'Refer',
  'Refer Text Book',
  'Refer Txt Book',
  'Review',
  'Updated Contents',
]

const Titles: FC<{
  mainTitles: {
    description: string;
    id: string;
    subTitle: {
      description: string;
      id: string;
      title: string;
      icon?: string;
      orderNum: number;
      questionCount: number,
      doneQuestionCount: number,
      pdf?: string;
    }[];
    title: string;
    url?: string;
    pdf?: string;
  }[];
  category: string;
  grade: string;
  mainTitleCount: number;
  subTitleCount: number;
}> = ({ mainTitles, grade, category, mainTitleCount, subTitleCount }) => {
  const flagsContext = (require as any).context(
    '../../assets/images/icons/icons',
    false,
    /\.png$/,
  );
  const flagOptions: { file: string; src: string }[] = flagsContext
    .keys()
    .sort()
    .map((key: string) => ({
      file: key.replace('./', '').replace('.png', ''),
      src: flagsContext(key),
    }));
  const [titles, setTitles] = useState<
    {
      description: string;
      id: string;
      subTitle: any[];
      title: string;
      url?: string;
      pdf?: string;
    }[]
  >(mainTitles);
  const [disable, setDisable] = useState<boolean>(false);
  const connection = useConnection();
  const navigator = useNavigate();
  const user = useUserInfo();
  const [mainTitleId] = useQueryParam<string | undefined>('mainTitle');
  const lastScrolledIdRef = useRef<string | null>(null);

  const loadQ = useCallback((id: string) => {
    if (!user) {
      navigator('/login');
    } else {
      navigator(`/question/${id}`);
    }

    console.log(id);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setTitles(mainTitles);
    };
    fetchData();
  }, [connection, titles, mainTitles]);
  useEffect(() => {
    if (!mainTitleId || lastScrolledIdRef.current === mainTitleId) {
      return;
    }
    const element = document.getElementById(`main-title-${mainTitleId}`);
    if (!element) {
      return;
    }
    // Get the element's position relative to the viewport
    const elementPosition = element.getBoundingClientRect().top;

    // Calculate the final scroll position: current scroll position + element position - desired offset
    const offsetPosition = (elementPosition - 100);
    // element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth' // Adds smooth scrolling behavior
    });
    // window.setTimeout(() => {
    //   window.scrollBy({ top: -40, left: 0, behavior: 'smooth' });
    // }, 250);
    lastScrolledIdRef.current = mainTitleId;
  }, [mainTitleId, titles]);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        width: '100%',
        // maxHeight: '70vh',
        // overflowY: 'scroll',
        // boxShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 60px 4px',
        padding: matches ? '15px' : '20px 40px',
        borderRadius: '0',
        maxWidth: '1090px;',
        boxShadow: '-12px 0px 12px 0px #0000001A',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          marginBottom: 2
        }}
      >
        {/* Left Section */}
        <Box>
          <Typography
            variant="h4"
            sx={{ color: '#C44A1E', fontWeight: 500 }}
          >
            {category} - Year {grade}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', mt: 0.5 }}
          >
            Cambridge Curriculum&nbsp;&nbsp;|&nbsp;&nbsp;English Medium
          </Typography>
        </Box>

        {/* Right Section - Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            sx={{ borderRadius: 3, textTransform: 'none', px: 3, color: '#545454',  border: '1px solid  #D9D9D9' }}
          >
            {subTitleCount} Skills
          </Button>
          <Button
            variant="outlined"
            sx={{ borderRadius: 3, textTransform: 'none', px: 3, color: '#545454',  border: '1px solid  #D9D9D9' }}
          >
            {mainTitleCount} Lessons
          </Button>
          <Button
            variant="outlined"
            sx={{ borderRadius: 3, textTransform: 'none', px: 3, color: '#545454',  border: '1px solid  #D9D9D9' }}
          >
            Demo Videos
          </Button>
          <Button
            variant="outlined"
            sx={{ borderRadius: 3, textTransform: 'none', px: 3,  color: '#545454',  border: '1px solid  #D9D9D9' }}
          >
            Past Papers
          </Button>
        </Stack>
      </Box>

      {/* Divider */}
      {/* <Divider sx={{ mt: 2 }} />*/}
      <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
        <Masonry gutter={'5px'}>
          {titles.map((e, i) => (
            <Item
              key={i}
              id={`main-title-${e.id}`}
              style={{
                // boxShadow: '#CDF2CA 5px 5px, #FFDEFA 10px 10px,  #FFC898 15px 15px',
                boxShadow: 'unset'
              }}>
              <>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                  {!!e.url && e.url !== '' ? (
                    <h2 style={{ textAlign: 'left', paddingLeft: 10, color: '#545454', fontWeight: '400', margin: '5px 0' }}>
                      <a href={e.url} target='_blank' rel='noreferrer'>
                        {`${i + 1}) `}<span style={{ color: '#459401', fontWeight: '600', }}>{e.title}</span>
                      </a>
                    </h2>
                  ) : (
                    <h2 style={{ textAlign: 'left', paddingLeft: 10, color: '#545454', fontWeight: '400', margin: '5px 0' }}>
                      {`${i + 1}) `}<span style={{ color: '#459401', fontWeight: '600', }}>{e.title}</span>
                    </h2>
                  )}
                  {!!e.pdf && e.pdf !== '' && (
                    <a href={e.pdf} target='_blank' rel='noreferrer'>
                      <img src={'/pdf.svg'} style={{ width: '30px', marginLeft: '5px' }} />
                    </a>
                  )}
                </div>
                <List
                  sx={{
                    // listStyleType: 'decimal',
                    pl: '2px',
                    pt: 0,
                    fontSize: '16px',
                    // '& .MuiListItem-root': {
                    //   display: 'list-item',
                    // },
                  }}>
                  {e.subTitle.map((skill, key) => {
                    return (
                      <>
                        <ListItem
                          key={key}
                          style={{ cursor: 'pointer' }}
                          sx={{
                            padding: matches ? '4px' : '4px 8px',
                            // paddingLeft: matches ? '4px' : '16px',
                          }}
                          onClick={() => {
                            if (!disable) {
                              loadQ(skill.id);
                            }
                          }}
                          // primaryText={skill.titleLang(this.data.currentLang)}
                          // initiallyOpen={false}
                          // primaryTogglesNestedList={true}
                          // nestedItems={[
                          //   <p>{skill.descLang(this.data.currentLang)}</p>
                          // ]}
                        >
                          <div style={{ minWidth: '28px' }}>
                            {skill.bullet ? <div>▶ </div> : <div>{skill.icon && skill.icon !== '' ?
                              (icons.includes(skill.icon) ? (
                                    <Tooltip title={skill.icon}>
                                      <Avatar variant='square' title={skill.icon} src={flagOptions.find((e) => e.file === skill.icon)?.src}
                                              sx={{ width: 24, height: 24 }} />
                                    </Tooltip>)
                                  : (<Avatar variant='square' title={skill.icon} src={flagOptions.find((e) => e.file === skill.icon)?.src}
                                             sx={{ width: 24, height: 24 }} />)
                              )
                              : (`${+skill.orderNum}.`)}</div>}
                          </div>
                          <Link style={{ cursor: 'pointer', color: '#2D4F10', textDecoration: 'unset' }}>
                            <span className={'hover-underline'}>{' ' + skill.title}</span>
                            {skill.questionCount === 0 ?  <></> : user?.id ? (
                              <span style={{ color: '#575757', fontWeight: '400', fontSize: '11px', marginLeft: '5px' }}>{skill.doneQuestionCount} / {skill.questionCount}</span>
                            ): <span style={{ color: '#575757', fontWeight: '400', fontSize: '11px', marginLeft: '5px' }}>{skill.questionCount}</span>}
                          </Link>
                          {!!skill.pdf && skill.pdf !== '' && (
                            <a href={skill.pdf} target='_blank' rel='noreferrer' onClick={(ev) => ev.stopPropagation()}>
                              <img src={'/pdf.svg'} alt='pdf' style={{ width: '24px', marginLeft: '5px' }} />
                            </a>
                          )}
                        </ListItem>
                        {skill.subTitleGroup.length !== 0 && (
                          <List
                            sx={{
                              // listStyleType: 'decimal',
                              pl: '18px',
                              // '& .MuiListItem-root': {
                              //   display: 'list-item',
                              // },
                            }}>
                            {skill.subTitleGroup
                              .sort((a: any, b: any) => a.orderNum - b.orderNum)
                              .map((subSkill: any, key1: number) => {
                                return (
                                  <ListItem
                                    key={key1}
                                    style={{ cursor: 'pointer' }}
                                    sx={{
                                      padding: matches ? '4px' : '4px 8px',
                                      paddingLeft: matches ? '4px' : '16px',
                                    }}
                                    onClick={() => {
                                      if (!disable) {
                                        loadQ(subSkill.id);
                                      }
                                    }}
                                    // primaryText={skill.titleLang(this.data.currentLang)}
                                    // initiallyOpen={false}
                                    // primaryTogglesNestedList={true}
                                    // nestedItems={[
                                    //   <p>{skill.descLang(this.data.currentLang)}</p>
                                    // ]}
                                  >
                                    <div style={{ minWidth: '28px' }}>
                                      {subSkill.bullet ? <div>▶ </div> : <div>
                                        {subSkill.icon && subSkill.icon !== '' ?
                                          (icons.includes(subSkill.icon) ? (
                                            <Tooltip title={subSkill.icon}>
                                              <Avatar variant='square' title={subSkill.icon} src={flagOptions.find((e) => e.file === subSkill.icon)?.src}
                                                      sx={{ width: 24, height: 24 }} />
                                            </Tooltip>
                                          ) : (
                                            <Avatar variant='square' title={subSkill.icon} src={flagOptions.find((e) => e.file === subSkill.icon)?.src}
                                                    sx={{ width: 24, height: 24 }} />
                                          ))
                                          : (`${+subSkill.orderNum}.`)}
                                      </div>}
                                    </div>
                                    <Link style={{ cursor: 'pointer', paddingLeft: '11px', color: '#2D4F10', textDecoration: 'unset' }}>
                                      <span className={'hover-underline'}>{' ' + subSkill.title}</span>
                                      {subSkill.questionCount ==! 0 ?  <></> :  user?.id ? (
                                        <span style={{ color: '#575757', fontWeight: '400', fontSize: '11px', marginLeft: '5px' }}>{subSkill.doneQuestionCount} / {subSkill.questionCount}</span>
                                      ): <span style={{ color: '#575757', fontWeight: '400', fontSize: '11px', marginLeft: '5px' }}>{subSkill.questionCount}</span>}
                                    </Link>
                                    {!!subSkill.pdf && subSkill.pdf !== '' && (
                                      <a href={subSkill.pdf} target='_blank' rel='noreferrer' onClick={(ev) => ev.stopPropagation()}>
                                        <img src={'/pdf.svg'} alt='pdf' style={{ width: '24px', marginLeft: '5px' }} />
                                      </a>
                                    )}
                                  </ListItem>
                                );
                              })
                            }
                          </List>
                        )}
                      </>
                    );
                  })}
                </List>
              </>
            </Item>
          ))}
          {/* {heights.map((height, index) => (*/}
          {/*  <Item key={index} sx={{ height }}>*/}
          {/*    {index + 1}*/}
          {/*  </Item>*/}
          {/* ))}*/}
        </Masonry>
      </ResponsiveMasonry>
    </Box>
  );
};

export default Titles;
